import { motion } from 'framer-motion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Calendar, Heart, ShieldCheck, MapPin, Sparkles, Flame } from 'lucide-react';
import ImageGallery from '@/components/ImageGallery';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';

const SupplierFiorePage = () => {
  const { language } = useLanguage();
  const timelineEvents = [
    {
      year: "1940",
      title: language === 'PL' ? "Początki w cieniu wojny" : "Beginnings in the shadow of war",
      icon: <Flame className="w-5 h-5 text-amber-600" />,
      text: language === 'PL' ? "Historia zaczęła się w miejscowości Corato, gdzie Cataldo Fiore założył tradycyjny piec opalany drewnem. W czasie trudnych lat II wojny światowej lokalna społeczność przynosiła tam własne ciasto, by wspólnie wypiekać chleb." : "The story began in Corato, where Cataldo Fiore founded a traditional wood-fired oven. During the difficult years of World War II, the local community brought their own dough there to bake bread together.",
      image: "/suppliers/fiore/map-puglia.webp",
      imgAlt: "Mapa Puglia Włochy"
    },
    {
      year: language === 'PL' ? "Lata 50. i 60." : "1950s and 60s",
      title: language === 'PL' ? "Fundamenty pod rodzinną markę" : "Foundations for a family brand",
      icon: <Calendar className="w-5 h-5 text-amber-600" />,
      text: language === 'PL' ? "Syn Cataldo, Antonio Fiore, z dumą kontynuował tradycję ojca, przenosząc działalność do większego lokalu w samym centrum Andrii. To właśnie wtedy, obok powszedniego chleba, tradycyjne taralli zaczęły zyskiwać na znaczeniu i stały się stałym elementem bogatej oferty pieca." : "Cataldo's son, Antonio Fiore, proudly continued his father's tradition, moving the business to a larger premises right in the center of Andria. It was then that, alongside daily bread, traditional taralli began to gain importance and became a permanent element of the oven's rich offer."
    },
    {
      year: language === 'PL' ? "Od 1971" : "Since 1971",
      title: language === 'PL' ? "Wizja Giuseppe Fiore" : "Giuseppe Fiore's vision",
      icon: <Sparkles className="w-5 h-5 text-amber-600" />,
      text: language === 'PL' ? "Prawdziwy przełom nastąpił, gdy stery przejął syn Antonia, Giuseppe. Po powrocie ze służby wojskowej postawił sobie ambitny cel: uczynić z lokalnych, rzemieślniczych przekąsek taralli kultowy przysmak znany i uwielbiany w całych Włoszech." : "A real breakthrough came when Antonio's son, Giuseppe, took the helm. After returning from military service, he set himself an ambitious goal: to make local, artisanal taralli snacks a cult delicacy known and loved throughout Italy."
    },
    {
      year: "1990–1993",
      title: language === 'PL' ? "Narodziny nowoczesnej firmy" : "Birth of a modern company",
      icon: <ShieldCheck className="w-5 h-5 text-amber-600" />,
      text: language === 'PL' ? "W 1993 roku Giuseppe oficjalnie powołał do życia markę Fiore di Puglia, sprawnie przekształcając mały, rzemieślniczy warsztat w prężnie rozwijające się i nowoczesne przedsiębiorstwo piekarnicze." : "In 1993, Giuseppe officially launched the Fiore di Puglia brand, efficiently transforming a small artisan workshop into a thriving and modern bakery enterprise."
    },
    {
      year: language === 'PL' ? "Współczesność" : "Present",
      title: language === 'PL' ? "Ekspansja i czwarte pokolenie" : "Expansion and the fourth generation",
      icon: <Heart className="w-5 h-5 text-amber-600" />,
      text: language === 'PL' ? "Obecnie firma zarządza trzema zaawansowanymi technologicznie zakładami produkcyjnymi. Giuseppe z sukcesem prowadzi biznes wspólnie z czwórką swoich dzieci, pielęgnując sekrety tradycyjnych receptur dla kolejnych pokoleń." : "Currently, the company manages three technologically advanced production plants. Giuseppe successfully runs the business together with his four children, cultivating the secrets of traditional recipes for future generations.",
      image: "/suppliers/fiore/taralli-bowl.webp",
      imgAlt: "Tradycyjne taralli od Fiore di Puglia"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F2] text-[#4A3E3D]">
      <SEO 
        title={language === 'PL' ? "Fiore di Puglia - Tradycyjne Włoskie Przekąski | Włoska Piwnica" : "Fiore di Puglia - Traditional Italian Snacks | Włoska Piwnica"}
        description={language === 'PL' ? "Poznaj historię marki Fiore di Puglia, producenta wyjątkowych włoskich przekąsek z ponad 80-letnią tradycją. Spróbuj prawdziwych taralli." : "Discover the history of Fiore di Puglia, a producer of exceptional Italian snacks with over 80 years of tradition. Taste real taralli."}
        canonicalUrl="/dostawcy/fiore-di-puglia"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Fiore di Puglia",
          "url": "https://wloska-piwnica.pl/dostawcy/fiore-di-puglia",
          "logo": "https://wloska-piwnica.pl/suppliers/fiore/logo.png",
          "description": "Producent tradycyjnych włoskich przekąsek taralli z ponad 80-letnią tradycją.",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "Apulia",
            "addressCountry": "IT"
          }
        }}
      />
      <Header />
      
      <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-[#3C2F2F] text-white border-b border-[#ebdcb9]/20">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25" style={{ backgroundImage: `url('/suppliers/fiore/taralli-bowl.webp')` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#3C2F2F]/60 to-[#3C2F2F]" />
          
          <div className="container relative z-10 mx-auto px-4 max-w-5xl text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-8"
            >
              <img 
                src="/suppliers/fiore/logo-red.webp" 
                alt="Fiore di Puglia Logo" 
                className="h-32 md:h-40 object-contain bg-white p-3 rounded-2xl border border-white/20 shadow-xl"
              />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-6xl font-display font-bold mb-4 tracking-wide text-[#EAD8B1]"
            >
              Fiore di Puglia
            </motion.h1>
            
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="h-0.5 w-32 bg-[#D4A373] mx-auto rounded-full mb-6"
            />
            
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-lg md:text-xl text-[#F4F1EA]/80 font-serif max-w-2xl mx-auto italic"
            >
              {language === 'PL' ? "„Prawdziwy smak Apulii zamknięty w chrupiących taralli, wypiekanych według rodzinnej receptury od 1940 roku.”" : "\"The true taste of Apulia closed in crispy taralli, baked according to a family recipe since 1940.\""}
            </motion.p>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 bg-[#F4EFE6]">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="grid md:grid-cols-5 gap-8 items-center">
              <div className="md:col-span-3 space-y-6">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-[#8C6239]">{language === 'PL' ? "Sekret tradycyjnego wypieku" : "The secret of traditional baking"}</h2>
                <p className="text-lg leading-relaxed text-[#5C4D4C] font-serif">
                  {language === 'PL' ? "Fiore di Puglia to synonim włoskiej tradycji piekarniczej. Pochodząca z malowniczego regionu Apulii marka od czterech pokoleń dostarcza najwyższej jakości taralli i chleby. Stosowanie wyłącznie lokalnych składników – najlepszej mąki, oliwy z oliwek Extra Virgin i naturalnych przypraw – sprawia, że każda przekąska smakuje jak przygotowana w rodzinnym, tradycyjnym piecu." : "Fiore di Puglia is synonymous with the Italian baking tradition. Hailing from the picturesque region of Apulia, the brand has been delivering top-quality taralli and breads for four generations. Using only local ingredients - the best flour, Extra Virgin olive oil, and natural spices - makes every snack taste like it was prepared in a traditional family oven."}
                </p>
                <div className="flex items-center gap-3 text-sm text-[#8C6239]/80 font-semibold uppercase tracking-wider">
                  <MapPin className="text-[#D4A373] w-5 h-5" />
                  <span>Andria / Corato, Puglia, Włochy</span>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-center">
                <img 
                  src="/suppliers/fiore/map-puglia.webp" 
                  alt="Lokalizacja w Apulii" 
                  className="rounded-xl border border-[#D4A373]/30 shadow-md max-h-56 object-contain bg-white p-2"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Story Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center text-[#8C6239] mb-8 sm:mb-16">
              {language === 'PL' ? "Nasza Tradycja i Ewolucja" : "Our Tradition and Evolution"}
            </h2>

            <div className="relative border-l border-[#D4A373]/40 md:border-l-0 md:before:absolute md:before:left-1/2 md:before:top-0 md:before:h-full md:before:w-0.5 md:before:bg-[#D4A373]/40 space-y-16">
              {timelineEvents.map((event, index) => {
                const isEven = index % 2 === 0;
                return (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className={`relative pl-8 md:pl-0 md:grid md:grid-cols-2 md:gap-16 items-center ${isEven ? 'md:text-right' : ''}`}
                  >
                    {/* Timeline Node */}
                    <div className="absolute left-0 top-1.5 md:left-1/2 md:-translate-x-1/2 md:top-1/2 md:-translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-[#D4A373]/50 bg-[#FAF7F2] shadow-md">
                      {event.icon}
                    </div>

                    {/* Timeline Content Block */}
                    <div className={`${isEven ? 'md:col-start-1' : 'md:col-start-2'} space-y-3`}>
                      <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#D4A373]/15 text-[#8C6239] rounded-full">
                        {event.year}
                      </span>
                      <h3 className="text-2xl font-bold text-[#8C6239] font-display">{event.title}</h3>
                      <p className="text-[#5C4D4C] leading-relaxed font-serif text-lg">{event.text}</p>
                    </div>

                    {/* Visual Asset Block (for rows that have pictures) */}
                    {event.image ? (
                      <div className={`mt-4 md:mt-0 ${isEven ? 'md:col-start-2' : 'md:col-start-1 md:row-start-1'} flex justify-center`}>
                        <motion.img 
                          whileHover={{ scale: 1.03 }}
                          src={event.image} 
                          alt={event.imgAlt} 
                          className="rounded-xl shadow-lg border border-[#D4A373]/20 max-h-60 object-cover w-full max-w-sm"
                        />
                      </div>
                    ) : (
                      /* Empty spacing if no photo */
                      <div className="hidden md:block" />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Feature Section: Taralli Showcase */}
        <section className="bg-[#EAD8B1]/20 py-16 border-t border-b border-[#D4A373]/20">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="/suppliers/fiore/taralli-bowl.webp" 
                  alt="Tradycyjne taralli" 
                  loading="lazy"
                  className="rounded-2xl shadow-xl border border-[#D4A373]/20"
                />
              </div>
              <div className="space-y-6">
                <span className="text-xs font-bold uppercase tracking-widest text-[#D4A373] bg-[#D4A373]/10 px-3 py-1 rounded-full">
                  {language === 'PL' ? "Tradycyjna przekąska" : "Traditional snack"}
                </span>
                <h3 className="text-2xl md:text-3xl font-display font-bold text-[#8C6239]">{language === 'PL' ? "Dlaczego Taralli od Fiore?" : "Why Taralli from Fiore?"}</h3>
                <p className="text-lg leading-relaxed text-[#5C4D4C] font-serif">
                  {language === 'PL' ? "Taralli to chrupiące krążki chlebowe z oliwą i białym winem – kwintesencja apulijskiego rzemiosła. Receptura Fiore di Puglia stawia na najwyższą jakość składników, powolne wyrastanie ciasta oraz tradycyjną metodę wypieku, co gwarantuje niepowtarzalną kruchość i głęboki, śródziemnomorski smak. Idealnie komponują się z deską serów, wędlinami i dobrym winem." : "Taralli are crispy bread rings with olive oil and white wine - the quintessence of Apulian craftsmanship. The Fiore di Puglia recipe relies on the highest quality ingredients, slow dough rising, and a traditional baking method, guaranteeing unique crispness and deep, Mediterranean flavor. They pair perfectly with a cheese board, cold cuts, and good wine."}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Action Button */}
        <div className="container mx-auto px-4 max-w-4xl text-center pb-12 pt-8">
            <a href="https://www.fioredipuglia.com/en/company/" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#8C6239] text-white font-bold py-3 px-8 rounded-full hover:bg-[#8C6239]/90 transition-colors shadow-lg">
                {language === 'PL' ? "Dowiedz się więcej" : "Learn more"}
            </a>
        </div>

        {/* Gallery Section */}
        <ImageGallery 
          images={[
            "/suppliers/fiore/gallery/fio-1.webp",
            "/suppliers/fiore/gallery/fio-2.webp",
            "/suppliers/fiore/gallery/fio-3.webp",
            "/suppliers/fiore/gallery/fio-4.webp"
          ]} 
        />
      </main>

      <Footer />
    </div>
  );
};

export default SupplierFiorePage;
